import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Autocomplete } from '../components/Autocomplete';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { ClientLink, ProjectLink } from '../components/links/EntityLinks';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Edit, Trash2, MoreVertical, FileText, IndianRupee, AlertTriangle, Loader2, Search, X, Send, MailCheck, CreditCard, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirmation } from '../components/ConfirmationProvider';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDaysUntil } from '../lib/formatUtils';
import { Pagination } from '../components/Pagination';
import { usePageSize } from '../context/PageSizeContext';
import { useDebounce } from '../lib/useDebounce';
import { ReminderBlock, StatusBadge, renewalCycleOptions, cycleMonths, cycleShortLabel } from './HostingPage';
import RemindersCell from '../components/RemindersCell';
import { PaymentLinkDialog } from '../components/payments/PaymentLinkDialog';
import { AMCQuoteDialog } from '../components/amc/AMCQuoteDialog';

// AMC reuses the unified cycle list from HostingPage but renames "Yearly"
// to "Annually" to match the historical AMC vocabulary stored on rows.
const cycleOptions = renewalCycleOptions.map(o =>
  o.value === 'yearly' ? { ...o, label: 'Annually' } : o
);
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'deactivated', label: 'Deactivated' },
];
const DEFAULT_TRIGGERS = [30, 7, 1];

const emptyForm = {
  title: '', type: 'amc',
  project_id: '', project_name: '', client_id: '', client_name: '',
  amount: '', billing_cycle: 'monthly',
  start_date: '', status: 'active', description: '',
  send_reminders: false,
  reminder_emails: [],
  reminder_triggers: [...DEFAULT_TRIGGERS],
};

const formatCurrency = (a) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(a || 0);

// Client-side preview only — backend recomputes authoritatively.
// Mirrors backend `_amc_compute_end_date` — year-end-inclusive
// convention (start + cycle - 1 day) so the preview agrees with what
// the backend will actually persist on save.
const computeEnd = (startDate, cycle) => {
  if (!startDate) return '';
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return '';
  d.setMonth(d.getMonth() + cycleMonths(cycle));
  d.setDate(d.getDate() - 1);  // last day INCLUSIVE
  return d.toISOString().split('T')[0];
};

export const AMCPage = () => {
  const navigate = useNavigate();
  const confirm = useConfirmation();
  const { pageSize } = usePageSize();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('amc_contracts.create');
  const canEdit = hasPermission('amc_contracts.edit');
  const canDelete = hasPermission('amc_contracts.delete');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expiryFrom, setExpiryFrom] = useState('');
  const [expiryTo, setExpiryTo] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedAMC, setSelectedAMC] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [emailInput, setEmailInput] = useState('');
  const [payLinkDialog, setPayLinkDialog] = useState({ open: false, amc: null });
  const [quoteDialog, setQuoteDialog] = useState({ open: false, amc: null });

  useEffect(() => { setPage(1); }, [debouncedSearch, statusFilter, expiryFrom, expiryTo, pageSize]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
        sort_by: 'end_date',
        sort_order: 'asc',
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (expiryFrom) params.set('end_date_gte', expiryFrom);
      if (expiryTo) params.set('end_date_lte', `${expiryTo}T23:59:59`);
      const res = await api.get(`/amc?${params.toString()}`);
      if (res.data.success) {
        setRows(res.data.data || []);
        setTotal(res.data.total ?? (res.data.data || []).length);
      }
    } catch {
      setRows([]); setTotal(0);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ },
    [page, pageSize, debouncedSearch, statusFilter, expiryFrom, expiryTo]);

  const handleProjectSelect = async (projectName) => {
    setFormData(f => ({ ...f, project_name: projectName, project_id: '' }));
    if (!projectName) return;
    try {
      const res = await api.get(`/projects?search=${encodeURIComponent(projectName)}&limit=10`);
      if (!res.data.success) return;
      const match = (res.data.data || []).find(p => (p.name || '') === projectName);
      if (!match) return;
      let emails = [];
      if (match.client_id) {
        try {
          const cRes = await api.get(`/clients/${match.client_id}`);
          if (cRes.data.success) {
            const c = cRes.data.data;
            if (c.email) emails.push(c.email);
            // billing_emails can be either legacy strings or the newer
            // `{email, role, label}` shape — normalise to strings so the
            // reminder chips render plain text (React error #31 otherwise).
            if (Array.isArray(c.billing_emails)) {
              c.billing_emails.forEach(entry => {
                const addr = typeof entry === 'string' ? entry : (entry && entry.email);
                if (addr && !emails.includes(addr)) emails.push(addr);
              });
            }
          }
        } catch { /* non-fatal */ }
      }
      setFormData(f => ({
        ...f,
        project_id: match.id,
        client_id: match.client_id || '',
        client_name: match.client_name || '',
        reminder_emails: f.reminder_emails && f.reminder_emails.length ? f.reminder_emails : emails,
      }));
    } catch { /* swallow */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.project_name) { toast.error('Project is required'); return; }
    if (!formData.title) { toast.error('AMC Title is required'); return; }
    if (!formData.start_date) { toast.error('Start date is required'); return; }
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount) || 0,
        reminder_triggers: (formData.reminder_triggers || []).map(v => parseInt(v, 10)).filter(v => !Number.isNaN(v) && v > 0).slice(0, 3),
      };
      if (isEditing && selectedAMC) {
        await api.put(`/amc/${selectedAMC.id}`, payload);
        toast.success('AMC updated');
      } else {
        await api.post('/amc', payload);
        toast.success('AMC created');
      }
      fetchData();
      setIsDialogOpen(false);
      resetForm();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (a) => {
    const confirmed = await confirm({ title: 'Delete AMC/Retainer', description: 'Delete this AMC/Retainer? This cannot be undone.' });
    if (!confirmed) return;
    try { await api.delete(`/amc/${a.id}`); toast.success('AMC deleted'); fetchData(); } catch { toast.error('Failed to delete'); }
  };

  const handleRaiseProforma = (a) => {
    if (!a.project_id) { toast.error('No project linked — cannot raise an invoice'); return; }
    const description = `AMC services — ${a.title || 'maintenance'} for the period ${formatDate(a.start_date)} to ${formatDate(a.end_date)}`;
    const qs = new URLSearchParams({
      project: a.project_id,
      type: 'proforma',
      description,
      amount: String(a.amount || 0),
    });
    navigate(`/invoices/new?${qs.toString()}`);
  };

  const handleSendReminder = async (a, mode) => {
    // mode === 'live' → email the configured recipient list now
    // mode === 'test' → email ONLY the operator's own address (preview)
    if (mode === 'live' && (!a.send_reminders || !(a.reminder_emails || []).length)) {
      toast.error('Renewal Reminders are off or no recipients are set on this AMC.');
      return;
    }
    const tid = toast.loading(mode === 'test' ? 'Sending test preview…' : 'Sending reminder…');
    try {
      const body = mode === 'test' ? { mode: 'test' } : { mode: 'live' };
      const res = await api.post(`/renewals/amc_contracts/${a.id}/send-reminder`, body);
      if (res.data.success) {
        toast.success(res.data.message, { id: tid });
      } else {
        toast.error(res.data.message || 'Send failed', { id: tid });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data?.detail || 'Send failed', { id: tid });
    }
  };

  const handleEdit = (a) => {
    setSelectedAMC(a);
    setFormData({
      title: a.title || a.project_name || '',
      type: a.type || 'amc',
      project_id: a.project_id || '',
      project_name: a.project_name || '',
      client_id: a.client_id || '',
      client_name: a.client_name || '',
      amount: String(a.amount || ''),
      // Normalise legacy "annually" → "yearly" so the unified cycle
      // dropdown can match. Backend `_amc_compute_end_date` accepts both.
      billing_cycle: (a.billing_cycle === 'annually' ? 'yearly' : (a.billing_cycle || 'monthly')),
      start_date: a.start_date ? a.start_date.split('T')[0] : '',
      status: a.status || 'active',
      description: a.description || '',
      send_reminders: !!a.send_reminders,
      reminder_emails: Array.isArray(a.reminder_emails)
        ? a.reminder_emails.map(entry => typeof entry === 'string' ? entry : (entry && entry.email) || '').filter(Boolean)
        : [],
      reminder_triggers: Array.isArray(a.reminder_triggers) && a.reminder_triggers.length ? a.reminder_triggers : [...DEFAULT_TRIGGERS],
    });
    setIsEditing(true);
    setIsDialogOpen(true);
  };

  const resetForm = () => { setFormData(emptyForm); setSelectedAMC(null); setIsEditing(false); setEmailInput(''); };

  const addEmail = (raw) => {
    const e = (raw || '').trim().toLowerCase();
    if (!e) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) { toast.error('Invalid email'); return; }
    if (formData.reminder_emails.includes(e)) return;
    setFormData(f => ({ ...f, reminder_emails: [...f.reminder_emails, e] }));
    setEmailInput('');
  };
  const removeEmail = (e) => setFormData(f => ({ ...f, reminder_emails: f.reminder_emails.filter(x => x !== e) }));
  const handleEmailKey = (e) => {
    if (['Enter', ',', ' '].includes(e.key)) { e.preventDefault(); addEmail(emailInput); }
    else if (e.key === 'Backspace' && !emailInput && formData.reminder_emails.length) {
      removeEmail(formData.reminder_emails[formData.reminder_emails.length - 1]);
    }
  };
  const updateTrigger = (idx, val) => {
    const next = [...formData.reminder_triggers];
    next[idx] = val;
    setFormData(f => ({ ...f, reminder_triggers: next }));
  };
  const addTrigger = () => {
    if (formData.reminder_triggers.length >= 3) return;
    setFormData(f => ({ ...f, reminder_triggers: [...f.reminder_triggers, 7] }));
  };
  const removeTrigger = (idx) => {
    setFormData(f => ({ ...f, reminder_triggers: f.reminder_triggers.filter((_, i) => i !== idx) }));
  };

  const renderExpiryCell = (d) => {
    if (!d) return <span className="text-muted-foreground">—</span>;
    let info;
    try { info = formatDaysUntil(d); } catch { info = { label: '', tier: 'unknown' }; }
    const colorByTier = { expired: 'text-red-500', critical: 'text-red-500', warn: 'text-amber-500', ok: 'text-emerald-500', unknown: 'text-muted-foreground' };
    return (
      <div className="leading-tight">
        <div className="text-sm font-medium">{formatDate(d)}</div>
        <div className={`text-[11px] ${colorByTier[info.tier] || ''}`} data-testid="expiry-sublabel">{info.label}</div>
      </div>
    );
  };

  // Two-source preview for the end date:
  //   • Stored end_date (from DB) — the truth, including any renewals.
  //   • Computed end (start + cycle - 1 day) — what backend will write
  //     on save. Shown only when the user has actually changed
  //     start_date / billing_cycle, otherwise it would just duplicate
  //     the stored value or show stale math (e.g. a renewed AMC where
  //     stored end is far ahead of start + 1 year).
  const storedEnd = selectedAMC?.end_date ? selectedAMC.end_date.slice(0, 10) : '';
  const startChanged = isEditing && selectedAMC && formData.start_date !== ((selectedAMC.start_date || '').slice(0, 10));
  const cycleChanged = isEditing && selectedAMC && (formData.billing_cycle !== (selectedAMC.billing_cycle === 'annually' ? 'yearly' : (selectedAMC.billing_cycle || 'monthly')));
  const previewEnd = computeEnd(formData.start_date, formData.billing_cycle);
  const showPreview = !isEditing || startChanged || cycleChanged;

  const stats = useMemo(() => {
    const activeCount = rows.filter(a => a.status === 'active').length;
    const activeRevenue = rows.filter(a => a.status === 'active').reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    return { activeCount, activeRevenue };
  }, [rows]);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="amc-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div><h1 className="text-2xl font-bold">AMC / Retainers</h1><p className="text-muted-foreground">Manage annual maintenance contracts and retainers linked to projects</p></div>
        <Dialog open={isDialogOpen} onOpenChange={(o) => { setIsDialogOpen(o); if (!o) resetForm(); }}>
          {canCreate && (<DialogTrigger asChild><Button data-testid="add-amc-btn"><Plus className="h-4 w-4 mr-2" />Add AMC/Retainer</Button></DialogTrigger>)}
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="amc-dialog">
            <DialogHeader><DialogTitle>{isEditing ? 'Edit AMC/Retainer' : 'Add AMC/Retainer'}</DialogTitle><DialogDescription>Link to a project</DialogDescription></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Project <span className="text-red-500">*</span></Label>
                <Autocomplete
                  value={formData.project_name}
                  onChange={handleProjectSelect}
                  fetchOptions={async (q) => { const res = await api.get(`/projects?search=${q}&limit=10`); return res.data.success ? res.data.data.map(p => p.name) : []; }}
                  placeholder="Search projects..."
                  data-testid="amc-project"
                />
                {formData.client_name && (
                  <p className="text-xs text-muted-foreground">Client: <span className="text-foreground font-medium">{formData.client_name}</span></p>
                )}
              </div>

              <div className="space-y-2">
                <Label>AMC Title <span className="text-red-500">*</span></Label>
                <Input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Quarterly Website Maintenance" required data-testid="amc-title" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Type</Label><Select value={formData.type} onValueChange={v => setFormData({ ...formData, type: v })}><SelectTrigger data-testid="amc-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="amc">AMC</SelectItem><SelectItem value="retainer">Retainer</SelectItem></SelectContent></Select></div>
                <div className="space-y-2"><Label>Amount (INR) / {cycleShortLabel(formData.billing_cycle)} *</Label><Input type="number" value={formData.amount} onChange={e => setFormData({ ...formData, amount: e.target.value })} required data-testid="amc-amount" /></div>
                <div className="space-y-2"><Label>Billing Cycle</Label><Select value={formData.billing_cycle} onValueChange={v => setFormData({ ...formData, billing_cycle: v })}><SelectTrigger data-testid="amc-billing-cycle"><SelectValue /></SelectTrigger><SelectContent>{cycleOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label>Status</Label><Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}><SelectTrigger data-testid="amc-status"><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2 col-span-2"><Label>Start Date *</Label><Input type="date" value={formData.start_date} onChange={e => setFormData({ ...formData, start_date: e.target.value })} required data-testid="amc-start-date" /></div>
              </div>

              {/* Existing AMCs always show their stored end (the
                  truth, including renewals). The computed preview
                  only appears when the user changes start/cycle so
                  it doesn't contradict the stored value. */}
              {isEditing && storedEnd && (
                <div className="text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2" data-testid="amc-end-stored">
                  End date: <span className="font-semibold text-foreground">{formatDate(storedEnd)}</span>
                  <span className="ml-1 opacity-70">(from last renewal / current contract)</span>
                </div>
              )}
              {showPreview && previewEnd && (
                <div className="text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2" data-testid="amc-end-preview">
                  Auto-calculated end {isEditing ? '(after save)' : ''}: <span className="font-semibold text-foreground">{formatDate(previewEnd)}</span>
                </div>
              )}

              <div className="space-y-2"><Label>Description</Label><Textarea rows={2} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Scope of work, deliverables..." data-testid="amc-description" /></div>

              <ReminderBlock formData={formData} setFormData={setFormData} emailInput={emailInput} setEmailInput={setEmailInput} addEmail={addEmail} removeEmail={removeEmail} handleEmailKey={handleEmailKey} updateTrigger={updateTrigger} addTrigger={addTrigger} removeTrigger={removeTrigger} />

              <DialogFooter><Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button><Button type="submit" data-testid="amc-submit-btn">{isEditing ? 'Update' : 'Create'}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card><CardContent className="py-4 space-y-3">
        <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search by AMC title, client, project..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="amc-search" /></div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]" data-testid="amc-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Expires From</Label>
            <Input type="date" value={expiryFrom} onChange={e => setExpiryFrom(e.target.value)} className="w-[160px]" data-testid="amc-expiry-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Expires To</Label>
            <Input type="date" value={expiryTo} onChange={e => setExpiryTo(e.target.value)} className="w-[160px]" data-testid="amc-expiry-to" />
          </div>
          {(statusFilter !== 'all' || expiryFrom || expiryTo || search) && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setStatusFilter('all'); setExpiryFrom(''); setExpiryTo(''); }} data-testid="amc-filter-reset"><X className="h-3 w-3 mr-1" />Reset</Button>
          )}
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-3 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><FileText className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold" data-testid="amc-total">{total}</p><p className="text-xs text-muted-foreground">Total</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><FileText className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold" data-testid="amc-active">{stats.activeCount}</p><p className="text-xs text-muted-foreground">Active (page)</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><IndianRupee className="h-5 w-5 text-blue-500" /></div><div><p className="text-xl font-bold" data-testid="amc-revenue">{formatCurrency(stats.activeRevenue)}</p><p className="text-xs text-muted-foreground">Active Revenue</p></div></div></CardContent></Card>
      </div>

      <Card><CardContent className="p-0">
        {loading ? <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div> :
        rows.length === 0 ? <div className="flex flex-col items-center justify-center h-64 text-muted-foreground"><FileText className="h-12 w-12 mb-4 opacity-50" /><p>No AMC/Retainers found</p></div> :
        <Table data-testid="amc-table"><TableHeader><TableRow><TableHead>AMC Title</TableHead><TableHead>Type</TableHead><TableHead>Project / Client</TableHead><TableHead>Amount</TableHead><TableHead>Cycle</TableHead><TableHead>Status</TableHead><TableHead>Ends</TableHead><TableHead>Reminders</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow></TableHeader>
        <TableBody>{rows.map(a => (
          <TableRow key={a.id} data-testid={`amc-row-${a.id}`}>
            <TableCell className="font-medium">{a.title || a.project_name || '—'}</TableCell>
            <TableCell><Badge variant="outline">{a.type}</Badge></TableCell>
            <TableCell>
              <div className="leading-tight">
                {a.project_id ? <ProjectLink id={a.project_id} name={a.project_name} /> : <span className="text-muted-foreground text-sm">—</span>}
                {a.client_name && <div className="text-[11px] text-muted-foreground"><ClientLink id={a.client_id} name={a.client_name} /></div>}
              </div>
            </TableCell>
            <TableCell className="font-semibold">{formatCurrency(a.amount)}</TableCell>
            <TableCell><Badge variant="outline" className="text-xs capitalize">{(a.billing_cycle || 'monthly').replace(/_/g, ' ')}</Badge></TableCell>
            <TableCell><StatusBadge value={a.status} /></TableCell>
            <TableCell>{renderExpiryCell(a.end_date)}</TableCell>
            <TableCell><RemindersCell row={a} testIdPrefix="amc-reminders" /></TableCell>
            <TableCell>
              <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`amc-actions-${a.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canEdit && (<DropdownMenuItem onClick={() => handleEdit(a)} data-testid={`amc-edit-${a.id}`}><Edit className="h-4 w-4 mr-2" />Edit</DropdownMenuItem>)}
                <DropdownMenuItem onClick={() => handleRaiseProforma(a)} data-testid={`amc-raise-${a.id}`} disabled={!a.project_id}><FileText className="h-4 w-4 mr-2" />Raise Proforma</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setQuoteDialog({ open: true, amc: a })} data-testid={`amc-quote-${a.id}`}><Wrench className="h-4 w-4 mr-2" />Generate Quote</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPayLinkDialog({ open: true, amc: a })} data-testid={`amc-paylink-${a.id}`}><CreditCard className="h-4 w-4 mr-2" />Send Payment Link</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSendReminder(a, 'live')} data-testid={`amc-send-reminder-${a.id}`} disabled={!a.send_reminders || !(a.reminder_emails || []).length}><Send className="h-4 w-4 mr-2" />Send Reminder Now</DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleSendReminder(a, 'test')} data-testid={`amc-send-test-${a.id}`}><MailCheck className="h-4 w-4 mr-2" />Send Test to Me</DropdownMenuItem>
                {canDelete && (<DropdownMenuItem onClick={() => handleDelete(a)} className="text-destructive focus:text-destructive" data-testid={`amc-delete-${a.id}`}><Trash2 className="h-4 w-4 mr-2" />Delete</DropdownMenuItem>)}
              </DropdownMenuContent></DropdownMenu>
            </TableCell>
          </TableRow>))}</TableBody></Table>}
        <div className="px-4 pb-3">
          <Pagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} testIdPrefix="amc-pagination" />
        </div>
      </CardContent></Card>

      {/* Razorpay payment link — same component used on Hosting page. */}
      <PaymentLinkDialog
        open={payLinkDialog.open}
        entity={payLinkDialog.amc}
        entityType="amc"
        onClose={() => setPayLinkDialog({ open: false, amc: null })}
      />

      {/* AMC quote generator + saved quote manager. */}
      <AMCQuoteDialog
        open={quoteDialog.open}
        amc={quoteDialog.amc}
        onClose={() => setQuoteDialog({ open: false, amc: null })}
      />
    </div>
  );
};

// Suppress unused-import lint: AlertTriangle reserved for future "expired" stat
void AlertTriangle;
